import { isSharedNoteCellKey } from '../cellKeys';
import { listEligibleIgnoreGroups } from '../engine/ignoreGroups';
import type { CaseInputs, SchemaEntry, View } from '../engine/types';
import { buildTemplate } from './buildTemplate';
import type { CaseFileCashPolicy, CaseFileLine, CaseFileOneTimeItem, CaseFileV1 } from './types';
import {
    blankCashPolicy,
    buildCaseFileLine,
    collectTemplateLineKeys,
    emptyLinesRecord,
    fileMonthFromLastClosedDate,
    ignoreGroupInputsStub,
    isBlankCashPolicyFile,
    lastClosedDateFromFileMonth,
} from './utils';

export interface SerializeCaseOptions {
    schema: SchemaEntry[];
    caseMeta: { name: string; description?: string; expires_on?: string | null };
    inputs: CaseInputs;
    notes: Record<string, string>;
    lastClosedMonth: string;
    schemaFingerprint: string;
}

function serializeCashPolicy(inputs: CaseInputs): CaseFileCashPolicy {
    const policy = inputs.cash_policy;
    if (!policy) {
        return blankCashPolicy();
    }
    return {
        enabled: policy.enabled,
        floor: {
            operating_minimum: policy.floor.operating_minimum,
            drawdown_allowance: policy.floor.drawdown_allowance,
        },
        distribution: {
            cadence: policy.distribution.cadence,
            anchor_date: policy.distribution.anchor_date,
            amount_per_period: policy.distribution.amount_per_period,
            ...(policy.distribution.one_time &&
            policy.distribution.one_time.date &&
            policy.distribution.one_time.amount
                ? { one_time: policy.distribution.one_time }
                : {}),
        },
        sweep: {
            enabled: policy.sweep.enabled,
            mode: policy.sweep.mode ?? 'excess',
            amount_per_period: policy.sweep.amount_per_period,
            yield_income_view: policy.sweep.yield_income_view,
            opening_mm_balance: policy.sweep.opening_mm_balance,
        },
        solver: {
            horizon_months: policy.solver.horizon_months,
            round_to: policy.solver.round_to,
        },
    };
}

function activeLineToFileLine(
    schema: SchemaEntry[],
    view: View,
    lineKey: string,
    inputs: CaseInputs,
    consolidatedOnly: string[],
    ignoreMembers?: string[],
): CaseFileLine | null {
    const state = inputs.drivers[view]?.[lineKey];
    if (!state?.active) {
        return null;
    }
    const stub = ignoreMembers
        ? ignoreGroupInputsStub({ parent: lineKey, members: ignoreMembers }, view)
        : undefined;
    const base = buildCaseFileLine(schema, lineKey, view, consolidatedOnly, stub, ignoreMembers);
    const active = state.active;
    const cache = state.by_type[active];
    base.driver = active;
    if (active === 'carry_forward' || active === 'ignore' || active === 'labor_buildup') {
        base.values = Array(36).fill(null);
        if (active === 'labor_buildup' && cache?.params) {
            base.params = { ...cache.params };
        } else {
            base.params = {};
        }
    } else if (cache) {
        if (cache.recipe) {
            base.values = { ...cache.recipe };
        } else {
            base.values = [...cache.values];
        }
        base.params = cache.params ? { ...cache.params } : {};
    }
    return base;
}

export function serializeCase(options: SerializeCaseOptions): CaseFileV1 {
    const { schema, caseMeta, inputs, notes, lastClosedMonth, schemaFingerprint } = options;
    const skeleton = buildTemplate({ schema, lastClosedMonth, schemaFingerprint });
    const lastClosedDate =
        lastClosedMonth.length === 7
            ? lastClosedDateFromFileMonth(lastClosedMonth)
            : lastClosedMonth;
    const consolidatedOnly = [...inputs.consolidated_only];
    const lines = emptyLinesRecord();
    const groups = listEligibleIgnoreGroups(schema);

    for (const spec of collectTemplateLineKeys(schema, groups)) {
        if (consolidatedOnly.includes(spec.lineKey) && spec.view !== 'consolidated') {
            continue;
        }
        const serialized = activeLineToFileLine(
            schema,
            spec.view,
            spec.lineKey,
            inputs,
            consolidatedOnly,
            spec.ignoreMembers,
        );
        if (serialized) {
            lines[spec.view][spec.lineKey] = serialized;
        } else if (!consolidatedOnly.includes(spec.lineKey) || spec.view === 'consolidated') {
            const stub = spec.ignoreMembers
                ? ignoreGroupInputsStub(
                      { parent: spec.lineKey, members: spec.ignoreMembers },
                      spec.view,
                  )
                : undefined;
            lines[spec.view][spec.lineKey] = buildCaseFileLine(
                schema,
                spec.lineKey,
                spec.view,
                consolidatedOnly,
                stub,
                spec.ignoreMembers,
            );
        }
    }

    const caseNotes: Record<string, string> = {};
    for (const [key, text] of Object.entries(notes)) {
        if (!isSharedNoteCellKey(key)) {
            caseNotes[key] = text;
        }
    }

    const oneTimeItems: CaseFileOneTimeItem[] = inputs.one_time_items.map((item) => {
        const mapped: CaseFileOneTimeItem = {
            month: item.month,
            view: item.view,
            line_key: item.line_key,
            amount: item.amount,
            mode: item.mode,
            id: item.id,
        };
        if (item.notes) {
            mapped.notes = item.notes;
        }
        return mapped;
    });

    const cashPolicy = serializeCashPolicy(inputs);
    const emittedCashPolicy =
        inputs.cash_policy === null || isBlankCashPolicyFile(cashPolicy)
            ? blankCashPolicy()
            : cashPolicy;

    return {
        ...skeleton,
        format_version: 2,
        case: {
            name: caseMeta.name,
            description: caseMeta.description ?? '',
            expires_on: caseMeta.expires_on ?? null,
        },
        basis: {
            ...skeleton.basis,
            last_closed_month: fileMonthFromLastClosedDate(lastClosedDate),
        },
        consolidated_only: consolidatedOnly,
        lines,
        one_time_items: oneTimeItems,
        notes: caseNotes,
        cash_policy: emittedCashPolicy,
    };
}
